import { useState, type ReactNode } from "react";
import { type RunListFilters } from "../../types";
import { hasActiveFilters } from "./controlBarUtils";
import { SortPill } from "./SortPill";
import { StatusPill } from "./StatusPill";
import { DatePill } from "../../../shared/components/DatePill/DatePill";
import styles from "./ControlBar.module.css";

type OpenPill = "sort" | "status" | "date" | null;

export interface ControlBarProps {
  filters: RunListFilters;
  onChange: (filters: RunListFilters) => void;
  onClear: () => void;
  triggerAction?: ReactNode;
}

export function ControlBar({
  filters,
  onChange,
  onClear,
  triggerAction,
}: ControlBarProps) {
  const [openPill, setOpenPill] = useState<OpenPill>(null);
  const showClear = hasActiveFilters(filters);

  return (
    <div className={styles.bar} data-testid="control-bar">
      {triggerAction && (
        <div className={styles.triggerAction}>{triggerAction}</div>
      )}
      <div className={styles.pills}>
        <SortPill
          sortBy={filters.sortBy}
          sortDirection={filters.sortDirection}
          isOpen={openPill === "sort"}
          onOpenChange={(open) => setOpenPill(open ? "sort" : null)}
          onChange={(sortBy, sortDirection) =>
            onChange({ ...filters, sortBy, sortDirection })
          }
        />
        <StatusPill
          status={filters.status}
          isOpen={openPill === "status"}
          onOpenChange={(open) => setOpenPill(open ? "status" : null)}
          onChange={(status) => onChange({ ...filters, status })}
        />
        <DatePill
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          isOpen={openPill === "date"}
          onOpenChange={(open) => setOpenPill(open ? "date" : null)}
          onChange={(dateFrom, dateTo) =>
            onChange({ ...filters, dateFrom, dateTo })
          }
        />
      </div>
      <div className={styles.spacer} />
      {showClear && (
        <button
          type="button"
          className={styles.clearAll}
          onClick={onClear}
          data-testid="clear-all-filters"
        >
          Clear All Filters
        </button>
      )}
    </div>
  );
}
